import { FC } from 'react';
import { Alert, Box, Divider, ProfileImage, ProfileInitials, Text } from '@hopskipdrive/react-components';

import {
  FareDetailsContainer,
  HighlightedFare,
  OtherValuesContainer,
  PerRiderFareDetailsContainer,
  RidersFareContainer,
} from './FareDetails.styles';

interface FareDetailsProps {
  rideInfo?: {
    distance: number | undefined;
    duration: number | undefined;
    disclaimerText: string;
    estimatedFare?: string;
    createdAt?: Date;
    updatedAt?: string;
    status?: string;
    cancellationReason?: string;
    totalFareWithCredits?: string;
    fareDetails?: FareDetailType[];
    rideCredits?: FareDetailType[];
    perRiderFareDetails?: PerRiderFareDetails;
  };
  rideDetails?: RideDetails;
}

export const FareDetailsTab: FC<FareDetailsProps> = ({ rideInfo, rideDetails }) => {
  const perRiderFareDetails = rideInfo?.perRiderFareDetails;
  const savingsMessage =
    rideDetails && rideDetails.tripTemplate
      ? `Compared to the cost of ${perRiderFareDetails?.riders.length} individual rides during the entire duration of the ride series.`
      : `Compared to the cost of ${perRiderFareDetails?.riders.length} individual rides.`;

  return !perRiderFareDetails ? (
    <FareDetailsContainer>
      {rideInfo?.fareDetails?.map((fare, idx) => (
        <Box display="flex" key={idx} alignItems="center" justifyContent="space-between">
          <Text variant="labelXS" textColor="secondaryText">
            {fare.label}
          </Text>
          <Text variant="labelL" textColor="secondary-hover">
            {fare.display}
          </Text>
        </Box>
      ))}
      {rideInfo?.disclaimerText && (
        <Text mt={3} align="center" textColor="secondaryText">
          {rideInfo?.disclaimerText}
        </Text>
      )}
    </FareDetailsContainer>
  ) : (
    <>
      <PerRiderFareDetailsContainer>
        {perRiderFareDetails.riders.length ? (
          <RidersFareContainer>
            {perRiderFareDetails.riders?.map((rider, idx) => (
              <Box display="flex" key={idx} alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex">
                  {rider?.profilePhoto && rider.profilePhoto.indexOf('default_photo') === -1 ? (
                    <ProfileImage size={16} imgSrc={rider.profilePhoto || ''} />
                  ) : (
                    <ProfileInitials key={rider?.name} size={16} fontSize={8} fullName={rider?.name || ''} />
                  )}
                  <Text variant="labelXS" textColor="secondaryText" ml={1}>
                    {rider.name}
                  </Text>
                </Box>
                <Text variant="labelS" textColor="secondary-active">
                  {idx > 0 ? `+${rider.fareSubtotal}` : rider.fareSubtotal}
                </Text>
              </Box>
            ))}
            {perRiderFareDetails.riders.length > 1 && (
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Text variant="labelXS" textColor="secondaryText">
                  Rider fares
                </Text>
                <Box>
                  <Divider sx={{ mt: '3px', mb: '6px' }} />
                  <Text variant="labelS" textColor="secondary-active">
                    {perRiderFareDetails.riderFaresTotal.faresSubtotal}
                  </Text>
                </Box>
              </Box>
            )}
          </RidersFareContainer>
        ) : null}
        <OtherValuesContainer>
          {perRiderFareDetails.riders.length ? (
            <HighlightedFare>
              <Text variant="labelXS" textColor="secondaryText">
                Rider fares
              </Text>

              <Text variant="labelS" textColor="secondary-active">
                {perRiderFareDetails.riderFaresTotal.faresSubtotal}
              </Text>
            </HighlightedFare>
          ) : null}
          {perRiderFareDetails.riders && perRiderFareDetails.riders.length < 2 ? (
            perRiderFareDetails.fees.map((fee, idx) => (
              <Box display="flex" key={idx} alignItems="center" justifyContent="space-between" mt={2} pr={3}>
                <Text variant="labelXS" textColor="secondaryText">
                  {fee.name}
                </Text>
                <Text variant="labelS" textColor="secondary-active">
                  +{fee.amount}
                </Text>
              </Box>
            ))
          ) : (
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} pr={3}>
              <Text variant="labelXS" textColor="secondaryText">
                Shared fees
              </Text>
              <Text variant="labelS" textColor="secondary-active">
                +{perRiderFareDetails.feesTotal}
              </Text>
            </Box>
          )}
          {perRiderFareDetails.tolls?.map((toll, idx) => (
            <Box display="flex" key={idx} alignItems="center" justifyContent="space-between" mt={2} pr={3}>
              <Text variant="labelXS" textColor="secondaryText">
                {toll.name}
              </Text>
              <Text variant="labelS" textColor="secondary-active">
                +{toll.amount}
              </Text>
            </Box>
          ))}
          {perRiderFareDetails.discountsTotal !== '$0.00' && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} pr={3}>
              <Text variant="labelXS" textColor="secondaryText">
                Full-car discount
              </Text>
              <Text variant="labelS" textColor="success">
                -{perRiderFareDetails.discountsTotal}
              </Text>
            </Box>
          )}
          {perRiderFareDetails.creditsTotal && perRiderFareDetails.creditsTotal !== '$0.00' && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} pr={3}>
              <Text variant="labelXS" textColor="secondaryText">
                Credits
              </Text>
              <Text variant="labelS" textColor="success">
                -{perRiderFareDetails.creditsTotal}
              </Text>
            </Box>
          )}
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
            <Text variant="labelXS" textColor="secondaryText">
              Fare total
            </Text>
            <Box pr={3}>
              {perRiderFareDetails.fareTotal !== '$0.00' && (
                <Divider sx={{ mt: '3px', mb: '6px' }} bgColor="info-main" />
              )}
              <Text variant="labelS" textColor="secondary-active">
                {perRiderFareDetails.fareTotal}
              </Text>
            </Box>
          </Box>
        </OtherValuesContainer>
      </PerRiderFareDetailsContainer>
      {rideDetails && perRiderFareDetails.savingsTotal !== '$0.00' && (
        <Alert
          data-testid="savings-alert"
          aria-label="savings alert"
          variant="standard"
          severity={'success'}
          title={`This ride saved you ~${perRiderFareDetails.savingsTotal}`}
          message={savingsMessage}
          sx={{ mb: '12px', mt: '24px' }}
        />
      )}
    </>
  );
};
